import { useState } from "react";
import axios from "axios";
import { Lock, Inbox, ArrowLeft, Mail, Reply, CheckCircle2, Send, Video } from "lucide-react";
import { Link } from "react-router-dom";

export default function InboxPage() {
  const [code, setCode] = useState("");
  const [messages, setMessages] = useState(null);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyStatus, setReplyStatus] = useState("idle");

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contact-messages`, {
        headers: { "X-Admin-Code": code },
      });
      setMessages(res.data);
      try {
        const lr = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/asda-leads`, {
          headers: { "X-Admin-Code": code },
        });
        setLeads(lr.data);
      } catch {}
    } catch {
      setError("Invalid access code.");
    }
    setLoading(false);
  };

  const openReply = (id) => {
    setReplyingTo(id);
    setReplyText("");
    setReplyStatus("idle");
  };

  const sendReply = async (id) => {
    setReplyStatus("sending");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contact-messages/${id}/reply`,
        { reply: replyText },
        { headers: { "X-Admin-Code": code } }
      );
      setMessages(messages.map((m) => (m.id === id ? { ...m, reply_text: replyText, replied_at: res.data.replied_at } : m)));
      setReplyingTo(null);
    } catch {
      setReplyStatus("error");
    }
  };

  return (
    <div className="inbox-page" data-testid="inbox-page">
      <div className="inbox-wrap">
        <Link to="/" className="inbox-back" data-testid="inbox-back-link"><ArrowLeft className="icon" /> Back to site</Link>
        <div className="eyebrow">Private</div>
        <h2 className="heading">Message inbox.</h2>

        {messages === null ? (
          <form className="inbox-gate" onSubmit={unlock} data-testid="inbox-gate">
            <p className="inbox-hint"><Lock className="icon" /> Enter your access code to view messages received through the contact form.</p>
            <div className="cf-row" style={{ gridTemplateColumns: "1fr auto" }}>
              <input type="password" required placeholder="Access code" value={code} data-testid="inbox-code-input"
                onChange={(e) => setCode(e.target.value)} />
              <button type="submit" className="cf-submit" disabled={loading} data-testid="inbox-unlock-btn">
                {loading ? "Checking..." : "Unlock"}
              </button>
            </div>
            {error && <p className="cf-status err" data-testid="inbox-error">{error}</p>}
          </form>
        ) : (
          <div className="inbox-list" data-testid="inbox-list">
            <p className="inbox-count"><Inbox className="icon" /> {messages.length} message{messages.length === 1 ? "" : "s"}</p>
            {messages.length === 0 && <p className="inbox-empty">No messages yet.</p>}
            {messages.map((m) => (
              <div className="inbox-item" key={m.id} data-testid="inbox-message">
                <div className="inbox-item-head">
                  <span className="inbox-name">{m.name}</span>
                  <span className="inbox-date">{new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <a href={`mailto:${m.email}`} className="inbox-email"><Mail className="icon" /> {m.email}</a>
                <p className="inbox-msg">{m.message}</p>

                {m.replied_at ? (
                  <div className="inbox-replied" data-testid="inbox-replied-badge">
                    <div className="inbox-replied-head"><CheckCircle2 className="icon" /> Replied · {new Date(m.replied_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                    <p className="inbox-replied-text">{m.reply_text}</p>
                  </div>
                ) : replyingTo === m.id ? (
                  <div className="inbox-reply-box" data-testid="inbox-reply-box">
                    <textarea rows={4} autoFocus placeholder={`Write your reply to ${m.name}...`} value={replyText}
                      data-testid="inbox-reply-input" onChange={(e) => setReplyText(e.target.value)} />
                    <div className="inbox-reply-actions">
                      <button className="cf-submit" disabled={replyStatus === "sending" || !replyText.trim()}
                        onClick={() => sendReply(m.id)} data-testid="inbox-reply-send-btn">
                        <Send className="icon" /> {replyStatus === "sending" ? "Sending..." : "Send reply"}
                      </button>
                      <button className="inbox-reply-cancel" onClick={() => setReplyingTo(null)} data-testid="inbox-reply-cancel-btn">Cancel</button>
                    </div>
                    {replyStatus === "error" && <p className="cf-status err">Could not deliver to this address — it may be invalid. The reply was not sent.</p>}
                  </div>
                ) : (
                  <button className="inbox-reply-btn" onClick={() => openReply(m.id)} data-testid="inbox-reply-btn">
                    <Reply className="icon" /> Reply
                  </button>
                )}
              </div>
            ))}

            <div className="inbox-count" style={{ marginTop: "3rem" }} data-testid="asda-leads-count"><Video className="icon" /> {leads.length} ASDA video access request{leads.length === 1 ? "" : "s"}</div>
            {leads.length === 0 && <p className="inbox-empty">No ASDA access requests yet.</p>}
            {leads.map((l) => (
              <div className="inbox-item asda-lead" key={l.id} data-testid="asda-lead">
                <div className="inbox-item-head">
                  <a href={`mailto:${l.email}`} className="inbox-email" style={{ marginBottom: 0 }}><Mail className="icon" /> {l.email}</a>
                  <span className="inbox-date">{new Date(l.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                {l.video && <p className="inbox-msg" style={{ fontSize: ".9rem", marginTop: ".3rem" }}>First requested: {l.video}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
